import React from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";

// Reusable inline editable cell with pencil/edit, save (check), cancel (x)
const EditableCell = ({ value, onSave, type = "text", error = null }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value ?? "");

  const startEdit = () => {
    setDraft(value ?? "");
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft(value ?? "");
    setEditing(false);
  };
  const saveEdit = () => {
    if (onSave) onSave(draft);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center">
          <span className={error ? "text-red-500" : ""}>{value}</span>
          <button
            type="button"
            aria-label="Edit"
            onClick={startEdit}
            className="ml-2 text-gray-500 hover:text-gray-700"
            title="Edit"
          >
            {/* Pencil icon */}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
        </div>
        {error && <span className="text-xs text-red-500 mt-1">{error}</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <input
        className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
        value={draft}
        onChange={(e) =>
          setDraft(
            type === "number"
              ? e.target.value.replace(/[^0-9.]/g, "")
              : e.target.value
          )
        }
        type={type}
      />
      <button
        type="button"
        aria-label="Save"
        onClick={saveEdit}
        className="text-green-600 hover:text-green-700"
        title="Save"
      >
        {/* Check icon */}
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </button>
      <button
        type="button"
        aria-label="Cancel"
        onClick={cancelEdit}
        className="text-gray-500 hover:text-gray-700"
        title="Cancel"
      >
        {/* X icon */}
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

// Minute Book Entry Table Component
const MinuteBookEntryTable = ({
  transactions,
  rows,
  setRows,
  originalRows,
  setOriginalRows,
}) => {
  const [validationErrors, setValidationErrors] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const { companyId } = useParams();
  const currentUser = useSelector((state) => state.userSlice);

  // Extract minute book entries from transactions

  const { registerOfApplicationsAndAllotments } = useSelector(
    (state) => state.minuteBookSlice
  );

  const getMinuteBookEntries = () => {
    const entries = [];

    // Helper function to find the last (highest) value from an array of objects
    const findLastValue = (items, field) => {
      if (!items || items.length === 0) return 0;
      const values = items
        .map((item) => item.currentAllottedClass?.[field])
        .filter((val) => val !== undefined && val !== null)
        .map((val) => Number(val));
      return values.length > 0 ? Math.max(...values) : 0;
    };

    // Get the starting point for allotment and certificate numbers
    const lastAllotmentNumber = findLastValue(
      registerOfApplicationsAndAllotments.items,
      "numberOfAllotmentLetter"
    );
    const lastShareCertNumber = findLastValue(
      registerOfApplicationsAndAllotments.items,
      "shareCertificateNumber"
    );

    transactions.forEach((transaction) => {
      // Check if this transaction should show minute book entries
      const shouldShowMinuteBook = [
        "privateWithMoreThanOneClass",
        "unallotedShares",
        "PVTwithOneClass",
        "existingAuth",
        "shareTransfer",
      ].includes(transaction.processType);

      if (shouldShowMinuteBook && transaction.shareCapitalChanges) {
        transaction.shareCapitalChanges.forEach((shareChange) => {
          if (shareChange.allocations) {
            shareChange.allocations.forEach((allocation) => {
              // Find the matching official in registerOfApplicationsAndAllotments
              const matchingOfficial =
                registerOfApplicationsAndAllotments.items?.find(
                  (official) => official._id === allocation.officialId
                );

              if (matchingOfficial) {
                // Skip entries with 0 shares
                const numberOfShares = allocation.allocatedShares || 0;
                if (numberOfShares === 0) {
                  return;
                }

                // Get full name based on official type
                let fullName = "";
                if (matchingOfficial.names.firstName) {
                  // Individual official
                  const {
                    firstName,
                    middleName = "",
                    lastName,
                  } = matchingOfficial.names;
                  fullName = `${firstName} ${middleName} ${lastName}`.trim();
                } else if (matchingOfficial.names.companyName) {
                  // Corporate official
                  fullName = matchingOfficial.names.companyName;
                }

                entries.push({
                  fullName: fullName || "N/A",
                  classOfShares: shareChange.classOfShares || "Ordinary",
                  numberOfShares,
                  officialId: matchingOfficial?._id,
                  folioNo:
                    matchingOfficial.memberRegisterFolio ||
                    `${entries.length + 1}a`,
                  // Temporary values - will be assigned after sorting
                  allotmentNo: 0,
                  shareCertNo: 0,
                  sn: 0,
                });
              }
            });
          }
        });
      }
    });

    // If no entries found, return empty array
    if (entries.length === 0) {
      return [];
    }

    // Objective 1 & 2: Sort entries according to priority rules
    const getClassPriority = (classOfShares) => {
      const normalizedClass = (classOfShares || "").toLowerCase();
      if (normalizedClass.includes("ordinary")) return 1; // Ordinary Shares first
      if (normalizedClass.includes("preference")) return 2; // Preference Shares second
      return 3; // Others last
    };

    const sortedEntries = entries.sort((a, b) => {
      // First priority: Class of shares (Ordinary -> Preference -> Others)
      const classComparison =
        getClassPriority(a.classOfShares) - getClassPriority(b.classOfShares);
      if (classComparison !== 0) return classComparison;

      // Second priority: Highest allocated shares first (descending)
      const sharesComparison = b.numberOfShares - a.numberOfShares;
      if (sharesComparison !== 0) return sharesComparison;

      // Third priority: Alphabetical by full name (ascending)
      return a.fullName.localeCompare(b.fullName);
    });

    // Assign serial numbers, allotment numbers, and certificate numbers after sorting
    let serialNumber = 1;
    let allotmentCounter = 1;
    let certCounter = 1;

    sortedEntries.forEach((entry, index) => {
      entry.sn = serialNumber++;
      entry.allotmentNo = lastAllotmentNumber + allotmentCounter++;
      entry.shareCertNo = lastShareCertNumber + certCounter++;
    });

    return sortedEntries;
  };

  const minuteBookEntries = React.useMemo(
    () => getMinuteBookEntries(),
    // Recompute when transactions change OR when registerOfApplicationsAndAllotments updates from the store
    [transactions, registerOfApplicationsAndAllotments]
  );

  const targetTransaction = React.useMemo(() => {
    const processTypesToCheck = [
      "privateWithMoreThanOneClass",
      "unallotedShares",
      "PVTwithOneClass",
      "existingAuth",
      "shareTransfer",
    ];
    return transactions.find((t) =>
      processTypesToCheck.includes(t.processType)
    );
  }, [transactions]);

  const persistMinuteBookDetails = async (entriesToPersist) => {
    try {
      if (!targetTransaction?._id || !companyId) return;
      const payload = {
        companyId,
        documentId: targetTransaction._id,
        minuteBookDetails: {
          entries: (entriesToPersist || rows).map((e) => ({
            sn: Number(e.sn) || 0,
            fullName: e.fullName,
            officialId: e.officialId,
            classOfShares: e.classOfShares,
            numberOfShares: Number(e.numberOfShares) || 0,
            allotmentNo: Number(e.allotmentNo) || 0,
            folioNo: String(e.folioNo || ""),
            shareCertNo:
              e.shareCertNo === ""
                ? undefined
                : Number(e.shareCertNo) || undefined,
            createdBy: currentUser?._id,
            updatedBy: currentUser?._id,
          })),
          initialized: true,
          lastSavedAt: new Date(),
          lastSavedBy: currentUser?._id,
        },
      };
      await handleCompanyMaintenanceFormData(
        "existing-request",
        payload,
        companyId
      );
    } catch (err) {
      console.error("Failed to persist minute book details", err);
    }
  };

  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      // Simulate async data loading
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Initial population: prefer transaction minuteBookDetails if present
      const trxEntries = targetTransaction?.minuteBookDetails?.entries || [];
      if (rows.length === 0) {
        if (trxEntries.length > 0) {
          const cloned = trxEntries.map((e) => ({ ...e }));
          setRows(cloned);
          setOriginalRows(cloned.map((e) => ({ ...e })));
        } else if (minuteBookEntries.length > 0) {
          const cloned = minuteBookEntries.map((e) => ({ ...e }));
          setRows(cloned);
          setOriginalRows(cloned.map((e) => ({ ...e })));
          await persistMinuteBookDetails(minuteBookEntries);
        }
      }
      setIsLoading(false);
    };

    loadData();
  }, [minuteBookEntries, targetTransaction]);

  const updateCell = (index, key, newValue) => {
    const numericValue = Number(newValue);

    // Clear previous validation errors for this field
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${index}-${key}`];
      return newErrors;
    });

    if (key === "numberOfShares") {
      handleNumberOfSharesEdit(index, numericValue);
    } else if (key === "allotmentNo") {
      handleAllotmentNumberEdit(index, numericValue);
    } else if (key === "shareCertNo") {
      handleShareCertificateNumberEdit(index, numericValue);
    } else {
      // For other fields, just update normally
      setRows((prev) => {
        const copy = prev.map((r) => ({ ...r }));
        copy[index] = {
          ...copy[index],
          [key]: newValue,
        };
        return copy;
      });
    }
  };

  const handleNumberOfSharesEdit = (index, newValue) => {
    const currentRow = rows[index];

    // Find the original allotted amount for this user + class combination from originalRows
    const originalUserEntry = originalRows.find(
      (row) =>
        row.fullName === currentRow.fullName &&
        row.classOfShares === currentRow.classOfShares
    );
    const originalValue = originalUserEntry?.numberOfShares || 0;

    // Clear previous validation errors for this field
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${index}-numberOfShares`];
      return newErrors;
    });

    // Validation: Must be at least 1 (except for duplicates which can be 0)
    const isOriginalEntry = originalRows.find(
      (row) =>
        row.fullName === currentRow.fullName &&
        row.classOfShares === currentRow.classOfShares &&
        row.allotmentNo === currentRow.allotmentNo
    );

    if (newValue < 1) {
      if (isOriginalEntry) {
        setValidationErrors((prev) => ({
          ...prev,
          [`${index}-numberOfShares`]: "Original entry cannot be set to 0",
        }));
        return;
      } else if (newValue < 0) {
        setValidationErrors((prev) => ({
          ...prev,
          [`${index}-numberOfShares`]: "Number of shares cannot be negative",
        }));
        return;
      }
    }

    // Calculate total shares for this user + class combination across all entries
    const userTotalShares = rows
      .filter(
        (row, idx) =>
          idx !== index &&
          row.fullName === currentRow.fullName &&
          row.classOfShares === currentRow.classOfShares
      )
      .reduce((total, row) => total + (row.numberOfShares || 0), 0);

    // Validation: Total shares cannot exceed original allotted amount for this person + class combination
    if (userTotalShares + newValue > originalValue) {
      setValidationErrors((prev) => ({
        ...prev,
        [`${index}-numberOfShares`]: `${currentRow.classOfShares} shares cannot exceed the original amount of ${originalValue}`,
      }));
      return;
    }

    if (newValue === 0) {
      // Handle setting to 0 - add shares back to previous entry and remove current
      const currentShares = currentRow.numberOfShares;

      setRows((prev) => {
        const newRows = prev.map((r) => ({ ...r }));

        // Find the previous entry for this user + class combination (lower allotment number)
        const userEntries = newRows
          .map((row, idx) => ({ row, idx }))
          .filter(
            ({ row }) =>
              row.fullName === currentRow.fullName &&
              row.classOfShares === currentRow.classOfShares
          )
          .sort((a, b) => a.row.allotmentNo - b.row.allotmentNo);

        const currentEntryIndex = userEntries.findIndex(
          ({ idx }) => idx === index
        );

        if (currentEntryIndex > 0) {
          // Add shares back to the previous entry (same person + class)
          const previousEntry = userEntries[currentEntryIndex - 1];
          newRows[previousEntry.idx] = {
            ...newRows[previousEntry.idx],
            numberOfShares:
              Number(newRows[previousEntry.idx].numberOfShares || 0) +
              Number(currentShares || 0),
          };
        }

        // Remove the current entry
        newRows.splice(index, 1);

        return newRows;
      });
    } else if (newValue < currentRow.numberOfShares) {
      // Create one duplicate entry with the balance
      const balanceShares = currentRow.numberOfShares - newValue;

      setRows((prev) => {
        const copy = [...prev];
        const newRows = [...copy];

        // Update the current row with reduced shares
        newRows[index] = {
          ...currentRow,
          numberOfShares: newValue,
        };

        // Create duplicate entry (temporarily without final numbers)
        const duplicateEntry = {
          ...currentRow,
          numberOfShares: balanceShares,
          sn: 0, // Will be reassigned
        };

        // Insert duplicate entry right after the current row
        newRows.splice(index + 1, 0, duplicateEntry);

        // FIRST: Sort the table by current allotment numbers to establish order
        const sortedRows = newRows
          .map((r) => ({ ...r }))
          .sort((a, b) => {
            if (a.allotmentNo !== b.allotmentNo) {
              return a.allotmentNo - b.allotmentNo;
            }
            return a.fullName.localeCompare(b.fullName);
          });

        // SECOND: Apply sequential numbering to the sorted table
        const minThreshold = Math.min(
          ...originalRows.map((row) => row.allotmentNo)
        );
        const minCertThreshold = Math.min(
          ...originalRows.map((row) => row.shareCertNo)
        );

        sortedRows.forEach((row, idx) => {
          row.sn = idx + 1;
          row.allotmentNo = minThreshold + idx;
          row.shareCertNo = minCertThreshold + idx;
        });

        persistMinuteBookDetails(sortedRows);
        return sortedRows;
      });
    } else {
      // If increasing or same, just update the value
      setRows((prev) => {
        const copy = prev.map((r) => ({ ...r }));
        copy[index] = {
          ...copy[index],
          numberOfShares: newValue,
        };
        persistMinuteBookDetails(copy);
        return copy;
      });
    }
  };

  const handleAllotmentNumberEdit = (index, newValue) => {
    const currentRow = rows[index];

    // Find the minimum threshold (lowest original allotment number from all users)
    const minThreshold = Math.min(
      ...originalRows.map((row) => row.allotmentNo)
    );

    // Clear previous validation errors for this field
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${index}-allotmentNo`];
      return newErrors;
    });

    // Objective 4: Validate against the minimum threshold that started the allocation
    if (newValue < minThreshold) {
      setValidationErrors((prev) => ({
        ...prev,
        [`${index}-allotmentNo`]: `Allotment number cannot be less than ${minThreshold} (allocation threshold)`,
      }));
      return;
    }

    // Apply sort-first approach for consistency
    setRows((prev) => {
      const copy = prev.map((r) => ({ ...r }));

      // Set the new value for current row
      copy[index] = {
        ...copy[index],
        allotmentNo: newValue,
      };

      // FIRST: Sort by current allotment numbers to establish consistent order
      const sortedRows = copy
        .map((r) => ({ ...r }))
        .sort((a, b) => {
          if (a.allotmentNo !== b.allotmentNo) {
            return a.allotmentNo - b.allotmentNo;
          }
          return a.fullName.localeCompare(b.fullName);
        });

      // SECOND: Apply sequential numbering to the sorted data
      const minThreshold = Math.min(
        ...originalRows.map((row) => row.allotmentNo)
      );
      const minCertThreshold = Math.min(
        ...originalRows.map((row) => row.shareCertNo)
      );

      sortedRows.forEach((row, idx) => {
        row.sn = idx + 1;
        row.allotmentNo = minThreshold + idx;
        row.shareCertNo = minCertThreshold + idx;
      });

      persistMinuteBookDetails(sortedRows);
      return sortedRows;
    });
  };

  const handleShareCertificateNumberEdit = (index, newValue) => {
    const currentRow = rows[index];

    // Find the minimum threshold (lowest original certificate number from all users)
    const minThreshold = Math.min(
      ...originalRows.map((row) => row.shareCertNo)
    );

    // Clear previous validation errors for this field
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${index}-shareCertNo`];
      return newErrors;
    });

    // Validate against the minimum threshold that started the allocation
    if (newValue < minThreshold) {
      setValidationErrors((prev) => ({
        ...prev,
        [`${index}-shareCertNo`]: `Share Cert.No cannot be less than ${minThreshold} (allocation threshold)`,
      }));
      return;
    }

    // Check for duplicate share certificate numbers
    const duplicateIndex = rows.findIndex(
      (row, idx) => idx !== index && row.shareCertNo === newValue
    );

    if (duplicateIndex !== -1) {
      // For share certificates: leave the replaced field empty and sort
      setRows((prev) => {
        const copy = prev.map((r) => ({ ...r }));

        // Set the new value for current row
        copy[index] = {
          ...copy[index],
          shareCertNo: newValue,
        };

        // Leave the previous holder empty for manual editing
        copy[duplicateIndex] = {
          ...copy[duplicateIndex],
          shareCertNo: "", // Empty field
        };

        // Sort table: empty certificates first, then by allotment number
        const sortedRows = copy
          .map((r) => ({ ...r }))
          .sort((a, b) => {
            // Empty certificates come first
            if (a.shareCertNo === "" && b.shareCertNo !== "") return -1;
            if (a.shareCertNo !== "" && b.shareCertNo === "") return 1;

            // If both empty or both have values, sort by allotment number
            return a.allotmentNo - b.allotmentNo;
          });

        // Reassign serial numbers after sorting
        sortedRows.forEach((row, idx) => {
          row.sn = idx + 1;
        });

        persistMinuteBookDetails(sortedRows);
        return sortedRows;
      });
    } else {
      // Update the value if validation passes
      setRows((prev) => {
        const copy = prev.map((r) => ({ ...r }));
        copy[index] = {
          ...copy[index],
          shareCertNo: newValue,
        };

        // Sort table: empty certificates first, then by allotment number
        const sortedRows = copy
          .map((r) => ({ ...r }))
          .sort((a, b) => {
            // Empty certificates come first
            if (a.shareCertNo === "" && b.shareCertNo !== "") return -1;
            if (a.shareCertNo !== "" && b.shareCertNo === "") return 1;

            // If both empty or both have values, sort by allotment number
            return a.allotmentNo - b.allotmentNo;
          });

        // Reassign serial numbers after sorting
        sortedRows.forEach((row, idx) => {
          row.sn = idx + 1;
        });

        persistMinuteBookDetails(sortedRows);
        return sortedRows;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <SpinnerIcon />
        <span className="ml-3 text-gray-600">
          Loading minute book entries...
        </span>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="mb-4">
        <h4 className="text-sm text-gray-600">
          Share Allotment and Certificates Details
        </h4>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No minute book entries found for the current transactions.</p>
        </div>
      ) : (
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-primary">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Sn
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Full Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Class of Shares
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Number of Shares
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Allotment No.
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Folio No.
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Share Cert.No
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((entry, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {entry.sn}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {entry.fullName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {entry.classOfShares}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <EditableCell
                      value={entry.numberOfShares}
                      type="number"
                      error={validationErrors[`${index}-numberOfShares`]}
                      onSave={(val) => updateCell(index, "numberOfShares", val)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <EditableCell
                      value={entry.allotmentNo}
                      type="number"
                      error={validationErrors[`${index}-allotmentNo`]}
                      onSave={(val) => updateCell(index, "allotmentNo", val)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {entry.folioNo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <EditableCell
                      value={entry.shareCertNo}
                      type="number"
                      error={validationErrors[`${index}-shareCertNo`]}
                      onSave={(val) => updateCell(index, "shareCertNo", val)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MinuteBookEntryTable;
